import { ComplexPromotionTypes, PromotionType } from '../../graphQL/commons'
import type {
  IAvailabilityProduct,
  IAvailabilityPromotion,
} from '../../graphQL/queries/promotionItemsAvailability/types'
import {
  IAddAllAvailability,
  IProduct,
  IPromotion,
  IPromotionAddAllAvailability,
  IPromotionShortcut,
  IPromotionsAvailability,
} from '../../interfaces'
import { getPromotionFields } from '../../utils/common/getPromotionFields'
import { isNumber } from '../../utils/validTypes'
import { getPromotionItemsToAdd } from '../../utils/promotions/promotionItemsToAdd'

/**
 * The fulfilment rule behind a promotion's add-all button, as the promotions
 * service applies it:
 *
 * - **Buys, within a group: AND.** Every item of a buys group is required, in
 *   its quantity and, where the group names one, its package.
 * - **Buys, across groups: OR.** Any one complete group earns the promotion,
 *   which is why a multi-group promotion gets one button per group and each
 *   is decided on its own.
 * - **Fixed gifts: AND.** One gets group that is not `QUANTITY_SELECTION`
 *   delivers all of its items, so every one must be deliverable in full. A
 *   `DIRECT_PERCENTAGE` group is excluded: it discounts what is bought and
 *   delivers nothing.
 * - **Gift selection: OR.** A `QUANTITY_SELECTION` group (or several gets
 *   groups) lets the shopper choose, so one fully deliverable option is
 *   enough: one item of a `QUANTITY_SELECTION` group, or one whole group.
 *
 * **What the button sends is taken from the add-all action itself**
 * (`getPromotionItemsToAdd`), never re-derived. Every line it sends reaches
 * the cart as such, so it gets the full check: stock, package and bundle. A
 * gift it does not send reaches the cart only when the promotions service
 * adds it, where a bundle's options are not the obstacle, so those gifts get
 * the gift rule above on stock alone.
 *
 * A button is available only when its lines and a gift option are, **out of
 * the same stock**: a SKU that is bought and also gifted needs the two
 * quantities together, whether the button sends the gift or the service adds
 * it.
 *
 * **A gift the lookup did not return is unknown, not unavailable.**
 * `searchProduct` only returns products with an active price for the
 * customer, and a gift-only SKU often has none while being perfectly in
 * stock, so a missing gift neither disables a button nor appears in a reason.
 * A missing *buys* SKU is still unavailable, and so is a SKU that is both
 * bought and gifted.
 */

/** A cart line whose fulfilment the rule asks about. */
interface IRequiredLine {
  sku: string
  quantity: number
  productPackage: string | null
  /** Whether a bundle here blocks the button, which cannot choose options. */
  checkBundle: boolean
  /** Only a gift: may be missing from the lookup without blocking anything. */
  isGift: boolean
}

/**
 * What one line's check found. Everything but `ok` disables the button with
 * the out-of-stock reason; `missing` and `unknownPackage` are also anomalies,
 * reported because they point at data rather than at stock.
 */
type LineVerdict = 'ok' | 'outOfStock' | 'missing' | 'unknownPackage'

/**
 * Which quantity the product-level stock is compared with. `perLine` for a
 * request that carries each line's package (COMBO); `perSku` for one that
 * sends every line with `package: null` (COMPLEX), where the cart sees one
 * line per SKU carrying the sum across packages.
 */
type StockBasis = 'perLine' | 'perSku'

export type AddAllAnomalyKind =
  | 'missingBuysSku'
  | 'unknownPackage'
  | 'bundleInBuys'

/** A finding about the data behind a verdict, for the Datadog report. */
export interface IAddAllAnomaly {
  kind: AddAllAnomalyKind
  promotionId: string
  groupIdentifier: string | null
  sku: string
}

interface ILookup {
  products: Map<string, IAvailabilityProduct>
  /** The same promotions, re-read with the `productPackage` of their items. */
  promotions: Map<string, IAvailabilityPromotion>
  pageProduct: IProduct
}

interface ICheckContext {
  lookup: ILookup
  promotionId: string
  groupIdentifier: string | null
  /** Off where a missing buys SKU is expected (see the compute args). */
  reportMissingBuys: boolean
  anomalies: Map<string, IAddAllAnomaly>
}

const AVAILABLE: IAddAllAvailability = { available: true }

/** A missing or non-positive quantity still asks for one unit. */
const requiredQuantity = (quantity?: number | null): number =>
  isNumber(quantity) && quantity > 0 ? quantity : 1

const isNonEmpty = (names: string[]): names is [string, ...string[]] =>
  names.length > 0

/**
 * The same (SKU, package) asked for twice is one line in the summed quantity,
 * which is what the add-all action's own dedup sends and what the stock has
 * to cover.
 */
const mergeLines = (lines: IRequiredLine[]): IRequiredLine[] =>
  Array.from(
    lines
      .reduce<Map<string, IRequiredLine>>((acc, line) => {
        const key = JSON.stringify([line.sku, line.productPackage])
        const previous = acc.get(key)
        acc.set(
          key,
          previous
            ? {
                ...previous,
                quantity: previous.quantity + line.quantity,
                checkBundle: previous.checkBundle || line.checkBundle,
                isGift: previous.isGift && line.isGift,
              }
            : line,
        )
        return acc
      }, new Map())
      .values(),
  )

/**
 * Mirrors the storefront's own stock recheck on the prefill path the button
 * uses (storefront `v3.42.2`: `getItemsWithStock` in
 * `user/lib/cart/helpers/stock.ts`, with `getStockByPackage` from
 * `user/lib/storefront/helpers/getEntities.ts`):
 *
 * - A product the lookup did not return is dropped (`missing`), except for a
 *   gift-only line (see the rule above).
 * - `productQuantity` is compared with the **product's** stock first, even
 *   when a package is named.
 * - With a named package the line's own quantity is then compared with that
 *   package's stock; a package the product does not list is
 *   `unknownPackage`, which the cart reads as zero stock. The product's own
 *   `packageType` is not a fallback.
 *
 * The storefront runs that recheck only on tenants that manage stock, and on
 * the others `searchProduct` reports `stock: null`; `null` therefore means no
 * limit, and skips the package check too.
 *
 * `outOfStock: true` is unavailable whatever the number says — except on a
 * line that names a package where the stock is numeric. The storefront
 * derives that flag from the base unit (stock against the base `minQty` and
 * `jumpQty`), while a package brings its own, so a product flagged out of
 * stock can still fill a package line; there the numbers decide, as in the
 * cart.
 */
const lineStock = (
  { sku, quantity, productPackage, isGift }: IRequiredLine,
  { products }: ILookup,
  productQuantity: number,
): LineVerdict => {
  const product = products.get(sku)
  if (!product) return isGift ? 'ok' : 'missing'
  const decidedByNumbers = !!productPackage && isNumber(product.stock)
  if (product.outOfStock === true && !decidedByNumbers) return 'outOfStock'
  if (!isNumber(product.stock)) return 'ok'
  if (productQuantity > product.stock) return 'outOfStock'
  if (productPackage) {
    const named = product.extraPackages?.find(
      ({ name }) => name === productPackage,
    )
    if (!named) return 'unknownPackage'
    if (quantity > (named.stock ?? 0)) return 'outOfStock'
  }
  return 'ok'
}

const isBundleProduct = (sku: string, { products }: ILookup): boolean =>
  !!products.get(sku)?.bundle?.items?.length

const displayName = (sku: string, lookup: ILookup): string =>
  lookup.products.get(sku)?.name ||
  (sku === lookup.pageProduct.sku ? lookup.pageProduct.name : '') ||
  sku

interface ILinesVerdict {
  outOfStock: string[]
  bundle: string[]
}

const recordAnomaly = (
  context: ICheckContext,
  kind: AddAllAnomalyKind,
  sku: string,
) => {
  const anomaly: IAddAllAnomaly = {
    kind,
    promotionId: context.promotionId,
    groupIdentifier: context.groupIdentifier,
    sku,
  }
  context.anomalies.set(
    JSON.stringify([kind, anomaly.promotionId, anomaly.groupIdentifier, sku]),
    anomaly,
  )
}

/** Checks lines that are all required. */
const checkAllLines = (
  lines: IRequiredLine[],
  context: ICheckContext,
  basis: StockBasis,
): ILinesVerdict => {
  const { lookup } = context
  const merged = mergeLines(lines)
  const perSku = merged.reduce<Map<string, number>>(
    (acc, { sku, quantity }) => acc.set(sku, (acc.get(sku) || 0) + quantity),
    new Map(),
  )
  return merged.reduce<ILinesVerdict>(
    (acc, line) => {
      const name = displayName(line.sku, lookup)
      const productQuantity =
        basis === 'perSku'
          ? (perSku.get(line.sku) ?? line.quantity)
          : line.quantity
      const verdict = lineStock(line, lookup, productQuantity)
      if (verdict === 'missing' && context.reportMissingBuys) {
        recordAnomaly(context, 'missingBuysSku', line.sku)
      }
      if (verdict === 'unknownPackage') {
        recordAnomaly(context, 'unknownPackage', line.sku)
      }
      if (verdict !== 'ok') {
        return { ...acc, outOfStock: [...acc.outOfStock, name] }
      }
      if (line.checkBundle && isBundleProduct(line.sku, lookup)) {
        if (!line.isGift) recordAnomaly(context, 'bundleInBuys', line.sku)
        return { ...acc, bundle: [...acc.bundle, name] }
      }
      return acc
    },
    { outOfStock: [], bundle: [] },
  )
}

const unique = (names: string[]): string[] => Array.from(new Set(names))

/**
 * Out of stock takes precedence over bundle: a bundle that is also out of
 * stock cannot be fixed by choosing its options, so that is the reason shown,
 * and only one reason line is ever given.
 */
const toAvailability = (...verdicts: ILinesVerdict[]): IAddAllAvailability => {
  const outOfStock = unique(verdicts.flatMap((verdict) => verdict.outOfStock))
  if (isNonEmpty(outOfStock)) {
    return { available: false, reason: 'outOfStock', products: outOfStock }
  }
  const bundle = unique(verdicts.flatMap((verdict) => verdict.bundle))
  if (isNonEmpty(bundle)) {
    return { available: false, reason: 'bundle', products: bundle }
  }
  return AVAILABLE
}

/**
 * The required lines together with each gift option in turn: available when
 * any one combination is. A fixed gift is the one-option case, and no gift at
 * all leaves the required lines alone. When none works, every product behind
 * any failure is named.
 */
const evaluate = (
  required: IRequiredLine[],
  giftOptions: IRequiredLine[][],
  context: ICheckContext,
  basis: StockBasis,
): IAddAllAvailability => {
  if (!giftOptions.length) {
    return toAvailability(checkAllLines(required, context, basis))
  }
  const verdicts = giftOptions.map((option) =>
    checkAllLines([...required, ...option], context, basis),
  )
  if (
    verdicts.some(
      ({ outOfStock, bundle }) => !outOfStock.length && !bundle.length,
    )
  ) {
    return AVAILABLE
  }
  return toAvailability(...verdicts)
}

/** The re-read group item for `sku`, which carries its `productPackage`. */
const findPackage = (
  lookupPromotion: IAvailabilityPromotion | undefined,
  side: 'customerBuys' | 'customerGets',
  groupIndex: number,
  identifier: string | null,
  sku: string,
): string | null => {
  const groups = lookupPromotion?.[side]?.groups || []
  const group =
    groups.find(
      (candidate) => !!identifier && candidate.identifier === identifier,
    ) || (identifier ? undefined : groups[groupIndex])
  return group?.items?.find((item) => item.sku === sku)?.productPackage || null
}

/** Quantity per SKU, reading a missing quantity as none. */
const quantityBySku = (
  items: { sku: string | null; quantity: number | null }[],
): Map<string, number> =>
  items.reduce<Map<string, number>>(
    (acc, { sku, quantity }) =>
      sku ? acc.set(sku, (acc.get(sku) || 0) + Number(quantity || 0)) : acc,
    new Map(),
  )

interface IAvailabilityArgs {
  promotion: IPromotion & { id: string }
  lookup: ILookup
  autoAdd: boolean
  reportMissingBuys: boolean
  anomalies: Map<string, IAddAllAnomaly>
}

/**
 * One verdict per buys group. The request is what the add-all action sends
 * for that group (it emits gifts only for one buys group and one gets group,
 * and only for some gets types and `autoAdd` values — see its transformers).
 *
 * **A gift line counts as sent per line**: when the action emits it and its
 * own quantity is above zero. `useAddPrefillCart` drops every line whose
 * quantity is not `> 0` before the request, and the storefront allows a null
 * or absent quantity on `QUANTITY_SELECTION`, `PERCENTAGE` and `AMOUNT`
 * items, so a gets group can mix gifts that reach the cart with gifts that
 * never do; only the first kind get the full check. Emission is read per SKU
 * — the SKU's sent quantity exceeds what the buys group alone asks for — so a
 * gift that is also a buys SKU, merged into one line by the action's dedup,
 * still counts. When no gift line is sent, the gifts fall to the gift rule on
 * stock alone.
 *
 * The product-level stock is compared per SKU because the COMPLEX request
 * sends every line with `package: null`. Each named `productPackage` is still
 * checked against that package's stock: the promotion counts only units in
 * the named package, so a group whose package is out of stock cannot be
 * completed even when loose units cover the request.
 */
const complexAvailability = ({
  promotion,
  lookup,
  autoAdd,
  reportMissingBuys,
  anomalies,
}: IAvailabilityArgs): IPromotionAddAllAvailability => {
  const { customerBuysGroups, customerGetsGroups } =
    getPromotionFields(promotion)
  const lookupPromotion = lookup.promotions.get(promotion.id)

  const giftGroups = customerGetsGroups.map(
    ({ identifier, items, type }, index) => ({
      type,
      // Whether each line survives the `quantity > 0` filter, by position.
      reachesCart: items
        .filter((item) => !!item.sku)
        .map(({ quantity }) => isNumber(quantity) && quantity > 0),
      lines: items
        .filter((item): item is typeof item & { sku: string } => !!item.sku)
        .map<IRequiredLine>(({ sku, quantity }) => ({
          sku,
          quantity: requiredQuantity(quantity),
          productPackage: findPackage(
            lookupPromotion,
            'customerGets',
            index,
            identifier,
            sku,
          ),
          checkBundle: true,
          isGift: true,
        })),
    }),
  )

  // Gifts the button does not send: the gift rule on stock alone. A
  // `DIRECT_PERCENTAGE` group discounts what is bought and delivers nothing,
  // so it has no gift to check. Several gets groups can only reach here from a
  // direct call: `getPromotionsShortcuts` draws no button for them.
  const unsentGiftOptions = giftGroups
    .filter(({ type }) => type !== ComplexPromotionTypes.DIRECT_PERCENTAGE)
    .flatMap(({ type, lines }) => {
      const stockOnly = lines.map((line) => ({ ...line, checkBundle: false }))
      return type === ComplexPromotionTypes.QUANTITY_SELECTION
        ? stockOnly.map((line) => [line])
        : [stockOnly]
    })
    .filter((option) => option.length > 0)

  const verdictFor = (
    { identifier, items }: (typeof customerBuysGroups)[number],
    index: number,
  ): IAddAllAvailability => {
    const context: ICheckContext = {
      lookup,
      promotionId: promotion.id,
      groupIdentifier: identifier,
      reportMissingBuys,
      anomalies,
    }
    const buysItems = items.filter(({ sku }) => !!sku)
    const buysLines = buysItems.map<IRequiredLine>(({ sku, quantity }) => ({
      sku,
      quantity: requiredQuantity(quantity),
      productPackage: findPackage(
        lookupPromotion,
        'customerBuys',
        index,
        identifier,
        sku,
      ),
      checkBundle: true,
      isGift: false,
    }))
    const sent = getPromotionItemsToAdd({
      promotion,
      groupIdentifier: identifier,
      autoAdd,
      selectedProduct: lookup.pageProduct,
    })
    const sentBySku = quantityBySku(sent)
    const buysBySku = quantityBySku(buysItems)
    const sentGiftLines = giftGroups.flatMap(({ lines, reachesCart }) =>
      lines.filter(
        ({ sku }, position) =>
          reachesCart[position] &&
          (sentBySku.get(sku) || 0) > (buysBySku.get(sku) || 0),
      ),
    )
    return sentGiftLines.length
      ? evaluate([...buysLines, ...sentGiftLines], [], context, 'perSku')
      : evaluate(buysLines, unsentGiftOptions, context, 'perSku')
  }

  // A group the action cannot address (no identifier, in a multi-group
  // promotion) gets no verdict, so its button keeps its enabled state.
  const groups = customerBuysGroups.reduce<Record<string, IAddAllAvailability>>(
    (acc, group, index) =>
      group.identifier
        ? { ...acc, [group.identifier]: verdictFor(group, index) }
        : acc,
    {},
  )

  const single = customerBuysGroups.length === 1
  let promotionVerdict: IAddAllAvailability
  if (single) {
    promotionVerdict = verdictFor(customerBuysGroups[0], 0)
  } else if (!customerBuysGroups.length) {
    promotionVerdict = evaluate(
      [],
      unsentGiftOptions,
      {
        lookup,
        promotionId: promotion.id,
        groupIdentifier: null,
        reportMissingBuys,
        anomalies,
      },
      'perSku',
    )
  } else {
    // Not read for a multi-group promotion, whose buttons are per group.
    const verdicts = Object.values(groups)
    promotionVerdict =
      verdicts.find(({ available }) => available) || verdicts[0] || AVAILABLE
  }

  return { promotion: promotionVerdict, groups }
}

const totalQuantity = (lines: IRequiredLine[]): number =>
  lines.reduce((sum, { quantity }) => sum + quantity, 0)

/**
 * A combo's button sends the selected product and, depending on `autoAdd`,
 * its gift, so those lines are what must be fulfillable. Where the button
 * does not send the gift, the promotions service adds it, out of the same
 * stock, so each gift option is checked together with the button's lines on
 * stock alone: one gets item is a fixed gift, several are a choice. With no
 * gets item the gift is the product itself, inside the quantity the button
 * already sends. The combo request carries each line's package, so the
 * product-level stock is compared per line.
 */
const comboAvailability = ({
  promotion,
  lookup,
  autoAdd,
  reportMissingBuys,
  anomalies,
}: IAvailabilityArgs): IPromotionAddAllAvailability => {
  const { customerGetsItems, customerGetsQuantity, customerGetsPackage } =
    getPromotionFields(promotion)
  const pageSku = lookup.pageProduct.sku

  const buttonLines = (candidate: IPromotion): IRequiredLine[] =>
    getPromotionItemsToAdd({
      promotion: candidate,
      autoAdd,
      selectedProduct: lookup.pageProduct,
    }).map<IRequiredLine>(({ sku, quantity, package: linePackage }) => ({
      sku,
      quantity: requiredQuantity(quantity),
      productPackage: linePackage?.name || null,
      checkBundle: true,
      isGift: sku !== pageSku,
    }))

  const sent = buttonLines(promotion)
  // What the button would send with no gift to give; if the real lines ask
  // for more, the button is sending the gift itself.
  const sentWithoutGift = buttonLines({
    ...promotion,
    customerGets: { ...promotion.customerGets, items: [] },
  })
  const buttonSendsGift = totalQuantity(sent) > totalQuantity(sentWithoutGift)

  const giftOptions = buttonSendsGift
    ? []
    : customerGetsItems.map<IRequiredLine[]>((sku) => [
        {
          sku,
          quantity: requiredQuantity(customerGetsQuantity),
          productPackage: customerGetsPackage,
          checkBundle: false,
          isGift: true,
        },
      ])

  const context: ICheckContext = {
    lookup,
    promotionId: promotion.id,
    groupIdentifier: null,
    reportMissingBuys,
    anomalies,
  }
  return {
    promotion: evaluate(sent, giftOptions, context, 'perLine'),
    groups: {},
  }
}

const isComboOrComplex = (
  promotion?: IPromotion | null,
): promotion is IPromotion =>
  promotion?.type === PromotionType.COMBO ||
  promotion?.type === PromotionType.COMPLEX

/**
 * The promotions whose add-all button is on screen. Only these are worth a
 * stock request: a promotion drawn without a button has nothing to disable.
 */
export const getAddAllPromotions = (
  shortcuts: IPromotionShortcut[] = [],
): IPromotion[] =>
  shortcuts
    .map(({ showButton, data }) => (showButton ? data.promotion : null))
    .filter(isComboOrComplex)
    .filter(
      (promotion, index, all) =>
        all.findIndex(({ id }) => id === promotion.id) === index,
    )

/**
 * Every SKU the check needs to see, sorted so the request — and the effect
 * that keys on it — does not move when the promotions merely re-render.
 */
export const collectAddAllSkus = (
  pageSku: string,
  promotions: IPromotion[],
): string[] => {
  if (!promotions.length) return []
  const skus = promotions.flatMap((promotion) => {
    const { customerBuysGroups, customerGetsGroups, customerGetsItems } =
      getPromotionFields(promotion)
    return [
      ...customerBuysGroups.flatMap(({ items }) => items.map(({ sku }) => sku)),
      ...customerGetsGroups.flatMap(({ items }) => items.map(({ sku }) => sku)),
      ...(promotion.type === PromotionType.COMBO ? customerGetsItems : []),
    ]
  })
  return Array.from(
    new Set([pageSku, ...skus].filter((sku): sku is string => !!sku)),
  ).sort()
}

interface IComputeAddAllAvailabilityArgs {
  pageProduct: IProduct
  promotions: IPromotion[]
  products: IAvailabilityProduct[]
  autoAdd: boolean
  /**
   * Whether a buys SKU missing from the answer is an anomaly worth reporting.
   * Off on tenants that hide out-of-stock products, where the search leaves
   * out every out-of-stock product and a missing buys SKU is the ordinary
   * out-of-stock case; the verdict is the same either way.
   */
  reportMissingBuys: boolean
}

export interface IAddAllAvailabilityResult {
  availability: IPromotionsAvailability
  anomalies: IAddAllAnomaly[]
}

/**
 * The availability of each promotion's add-all button, from the lookup, with
 * the anomalies found on the way.
 */
export const computeAddAllAvailability = ({
  pageProduct,
  promotions,
  products,
  autoAdd,
  reportMissingBuys,
}: IComputeAddAllAvailabilityArgs): IAddAllAvailabilityResult => {
  const lookupPromotions = new Map<string, IAvailabilityPromotion>()
  products.forEach((product) =>
    (product.promotions || []).forEach((promotion) => {
      if (promotion.id && !lookupPromotions.has(promotion.id)) {
        lookupPromotions.set(promotion.id, promotion)
      }
    }),
  )
  const lookup: ILookup = {
    products: new Map(products.map((product) => [product.sku, product])),
    promotions: lookupPromotions,
    pageProduct,
  }
  const anomalies = new Map<string, IAddAllAnomaly>()

  const availability: IPromotionsAvailability = {
    sku: pageProduct.sku,
    promotions: promotions.reduce<Record<string, IPromotionAddAllAvailability>>(
      (acc, promotion) => {
        const { id } = promotion
        if (!id) return acc
        const args: IAvailabilityArgs = {
          promotion: { ...promotion, id },
          lookup,
          autoAdd,
          reportMissingBuys,
          anomalies,
        }
        const verdict =
          promotion.type === PromotionType.COMBO
            ? comboAvailability(args)
            : complexAvailability(args)
        return { ...acc, [id]: verdict }
      },
      {},
    ),
  }
  return { availability, anomalies: Array.from(anomalies.values()) }
}
